/*
 * Shim per la sostituzione del logger di matrix-js-sdk con AppLogger.
 *
 * Questo file viene intercettato da webpack tramite alias:
 *   "matrix-js-sdk/src/logger$" → questo shim
 *
 * In questo modo, tutti i ~80 file che importano logger dall'SDK
 * riceveranno automaticamente il nostro AppLogger, senza modificare
 * nemmeno un import. TypeScript segue lo stesso percorso tramite tsconfig paths.
 *
 * Cosa viene sostituito:
 *   logger      → nostro AppLogger (con prefisso gerarchico e livello per istanza)
 *
 * Cosa viene reimplementato localmente (per evitare dipendenza circolare):
 *   LogSpan     → wrapper leggero per raggruppare log con un prefisso temporaneo
 *   DebugLogger → stub no-op (non usato nel codice applicativo)
 *
 * Perché non importiamo LogSpan dall'SDK?
 * Se importassimo da "matrix-js-sdk/src/logger", l'alias webpack
 * reindirizzerebbe di nuovo a questo stesso file → dipendenza circolare.
 * L'implementazione di LogSpan è triviale e sicura da copiare.
 */

import { type ILogger, logger as appLogger } from "./AppLogger";

// ─── logger ─────────────────────────────────────────────────────────────────

/**
 * Logger radice dell'applicazione, sostituisce il singleton di matrix-js-sdk.
 * Tutti i getChild() esistenti continueranno a funzionare normalmente:
 *   const log = logger.getChild("DeviceListener:");  // → [DeviceListener:] ...
 */
export const logger: ILogger = appLogger;

// ─── Interfacce / tipi (compatibilità con l'SDK) ────────────────────────────

/**
 * Interfaccia pubblica del logger, riesportata per retrocompatibilità
 * con il codice che importa il tipo Logger dall'SDK.
 */
export type Logger = ILogger;

/**
 * Interfaccia di base del logger (senza getChild), riesportata per
 * retrocompatibilità con il codice che usa BaseLogger come tipo parametro.
 */
export type BaseLogger = Omit<ILogger, "getChild">;

// ─── LogSpan ────────────────────────────────────────────────────────────────

/**
 * Raggruppamento leggero di log correlati tramite un prefisso temporaneo.
 *
 * Alternativa più semplice a getChild(): non registra il logger nel framework,
 * quindi non occupa memoria oltre al ciclo di vita dello span.
 *
 * Esempio d'uso:
 *   const span = new LogSpan(logger, "check_abc1");
 *   span.debug("inizio controllo"); // → [DeviceListener:] check_abc1: inizio controllo
 *
 * Nota: reimplementato localmente per evitare la dipendenza circolare
 * che si creerebbe importando dalla libreria (l'alias webpack ricondurrebbe qui).
 */
export class LogSpan implements BaseLogger {
    private readonly name: string;

    public constructor(
        private readonly parent: BaseLogger,
        name: string,
    ) {
        // Aggiunge ":" come separatore visivo dopo il nome dello span
        this.name = name + ":";
    }

    public trace(...msg: unknown[]): void {
        this.parent.trace(this.name, ...msg);
    }

    public debug(...msg: unknown[]): void {
        this.parent.debug(this.name, ...msg);
    }

    public info(...msg: unknown[]): void {
        this.parent.info(this.name, ...msg);
    }

    public warn(...msg: unknown[]): void {
        this.parent.warn(this.name, ...msg);
    }

    public error(...msg: unknown[]): void {
        this.parent.error(this.name, ...msg);
    }

    /** @deprecated Usa debug() al suo posto. */
    public log(...msg: unknown[]): void {
        this.parent.debug(this.name, ...msg);
    }
}

// ─── DebugLogger ────────────────────────────────────────────────────────────

/**
 * Stub per retrocompatibilità con il codice che importa DebugLogger dall'SDK.
 * Non è usato nel codice applicativo: il filtraggio avviene tramite AppLogger.
 * Delega semplicemente all'AppLogger radice.
 */
export class DebugLogger implements Logger {
    public constructor(private readonly delegate: ILogger = appLogger) {}

    public trace(...msg: unknown[]): void {
        this.delegate.trace(...msg);
    }

    public debug(...msg: unknown[]): void {
        this.delegate.debug(...msg);
    }

    public info(...msg: unknown[]): void {
        this.delegate.info(...msg);
    }

    public warn(...msg: unknown[]): void {
        this.delegate.warn(...msg);
    }

    public error(...msg: unknown[]): void {
        this.delegate.error(...msg);
    }

    /** @deprecated Usa debug() al suo posto. */
    public log(...msg: unknown[]): void {
        this.delegate.debug(...msg);
    }

    public getChild(namespace: string): ILogger {
        return this.delegate.getChild(namespace);
    }
}
